import React, { createContext, useContext, useState, useCallback } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { cn } from '../lib/utils';

// Toast კონტექსტი
const ToastContext = createContext(null);

// Toast ტიპები
const toastTypes = {
  success: {
    icon: CheckCircle,
    bgColor: 'bg-green-500',
    borderColor: 'border-green-600',
    textColor: 'text-green-50'
  },
  error: {
    icon: AlertCircle,
    bgColor: 'bg-red-500',
    borderColor: 'border-red-600',
    textColor: 'text-red-50'
  },
  warning: {
    icon: AlertTriangle,
    bgColor: 'bg-yellow-500',
    borderColor: 'border-yellow-600',
    textColor: 'text-yellow-50'
  },
  info: {
    icon: Info,
    bgColor: 'bg-blue-500',
    borderColor: 'border-blue-600',
    textColor: 'text-blue-50'
  }
};

// Toast კომპონენტი
function Toast({ toast, onDismiss }) {
  const type = toastTypes[toast.type] || toastTypes.info;
  const Icon = type.icon;

  React.useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss(toast.id);
    }, toast.duration || 5000);

    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onDismiss]);

  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg border p-4 shadow-lg transition-all animate-slide-up',
        type.bgColor,
        type.borderColor,
        type.textColor
      )}
      role="alert"
    >
      <Icon className="h-5 w-5 flex-shrink-0" />
      <div className="flex-1">
        {toast.title && (
          <p className="font-semibold mb-1">{toast.title}</p>
        )}
        <p className="text-sm opacity-90">{toast.message}</p>
      </div>
      <button
        onClick={() => onDismiss(toast.id)}
        className="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity"
        aria-label="დახურვა"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// Toast Provider
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((toast) => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      type: 'info',
      duration: 5000,
      ...toast
    };

    setToasts((prev) => [...prev, newToast]);
    return id;
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const updateToast = useCallback((id, updates) => {
    setToasts((prev) =>
      prev.map((toast) =>
        toast.id === id ? { ...toast, ...updates } : toast
      )
    );
  }, []);

  // Helper მეთოდები
  const success = useCallback((message, options = {}) => {
    return addToast({ type: 'success', message, ...options });
  }, [addToast]);

  const error = useCallback((message, options = {}) => {
    return addToast({ type: 'error', message, ...options });
  }, [addToast]);

  const warning = useCallback((message, options = {}) => {
    return addToast({ type: 'warning', message, ...options });
  }, [addToast]);

  const info = useCallback((message, options = {}) => {
    return addToast({ type: 'info', message, ...options });
  }, [addToast]);

  return (
    <ToastContext.Provider
      value={{
        addToast,
        dismissToast,
        updateToast,
        success,
        error,
        warning,
        info
      }}
    >
      {children}
      <div
        className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm"
        role="region"
        aria-label="შეტყობინებები"
      >
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            toast={toast}
            onDismiss={dismissToast}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// Hook ტოსტების გამოსაყენებლად
export function useToast() {
  const context = useContext(ToastContext);
  
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  
  return context;
}

// Export პირდაპირი გამოყენებისთვის
export const toast = {
  success: (message, options) => {
    const event = new CustomEvent('toast', { 
      detail: { type: 'success', message, ...options } 
    });
    window.dispatchEvent(event);
  },
  error: (message, options) => {
    const event = new CustomEvent('toast', { 
      detail: { type: 'error', message, ...options } 
    });
    window.dispatchEvent(event);
  },
  warning: (message, options) => {
    const event = new CustomEvent('toast', { 
      detail: { type: 'warning', message, ...options } 
    });
    window.dispatchEvent(event);
  },
  info: (message, options) => {
    const event = new CustomEvent('toast', { 
      detail: { type: 'info', message, ...options } 
    });
    window.dispatchEvent(event);
  }
};
